import { motion } from "framer-motion";
import { Library, Sparkles } from "lucide-react";

const REFS = [
  {
    k: "Park et al. 2023 · Generative Agents",
    d: "The memory-reflection-planning loop for language-model agents in a small town; evaluation by interviews and human raters over two simulated days; the authors note they could not rerun ablations because reruns diverge.",
  },
  {
    k: "Zhou et al. 2026 · Virtual Community (ICLR 2026)",
    d: "An open-world embodied multi-agent simulator with community tasks (commuting, assistance, influence); shows language-model planners failing at timed multi-leg transit and stronger models winning more friendships.",
  },
  {
    k: "Shen et al. 2020 · Requirements for virtual worlds",
    d: "Causal preservation, perceptive consistency, state persistency, hotspot contention.",
  },
  {
    k: "Nechesov et al. 2025 · Virtual cities, logical-probabilistic agents",
    d: "Chained probabilistic claims lose validity across agents; a performance score for logical consistency; the agent versus entity distinction (\u201chow\u201d against \u201cwhy\u201d).",
  },
  {
    k: "Bojić et al. · CERN for AI",
    d: "A contained laboratory for testing bounded autonomy and social dynamics; Theory of Reasoned Action (Fishbein and Ajzen) as a lens on intentions.",
  },
  {
    k: "Busemeyer & Bruza 2012 · Quantum Models of Cognition and Decision",
    d: "Beliefs as amplitudes, order effects and interference in judgement.",
  },
  {
    k: "Grimm et al. 2006/2020 (ODD) · Müller et al. 2013 (ODD+D)",
    d: "The standard for describing agent-based models and their decision rules; the kernel is documented in this form.",
  },
  {
    k: "Bostrom 2003 · the simulation argument",
    d: "The project borrows only the question of what an inside observer can know, stated as the \u201cNested Observer Protocol\u201d; it makes no claim about the argument itself.",
  },
];

const ADDS = [
  {
    t: "EXACT REPLAY",
    d: "A hash-chained ledger of every event, observation, belief version and model call, replayed byte for byte from genesis or any snapshot, failing closed on divergence. Prior systems could not rerun their own ablations.",
  },
  {
    t: "COUNTERFACTUALS WITH A NOISE FLOOR",
    d: "Forks at any day with a declared intervention, beside a fork with one extra random draw, compared at fixed horizons with micro and macro state split; a public page shows the current one.",
  },
  {
    t: "DECISIONS SEPARATED FROM WORDS",
    d: "Decisions and words are made by different models and both are audited: Jev's choices against the rules' own ranking, Qwen's lines against the town's facts. Neither model can act on the world except through a choice point the kernel opened.",
  },
  {
    t: "KERNEL-COMPOSED SPEECH ACTS",
    d: "Topic, act, stance and length chosen by rules from the residents' state; the model renders and its lines are validated; sycophancy is a measured parameter (the refusal share), not the model's manner.",
  },
  {
    t: "BELIEF PROVENANCE",
    d: "Hop depth and root observation on every belief version, a bound on hearsay, and a switchable quantum-probability belief model with a promotion rule written before the test.",
  },
  {
    t: "PROMISES AS A MEASURED DILEMMA",
    d: "Commitments kept or broken at their time, with trust consequences and memories of being let down.",
  },
  {
    t: "KNOWLEDGE APART FROM BEHAVIOUR",
    d: "Who learned of a change, through which channel, verified against the ledger; \u201cknew and went anyway\u201d as an explicit number; an interview harness that scores recall against the record with \u201cunknown\u201d separated from \u201cwrong\u201d.",
  },
  {
    t: "A BORDER FOR VISITORS",
    d: "Outside messages are observations that never become memories, so the residents' minds cannot be planted from outside.",
  },
];

export default function WhatsNew() {
  return (
    <section id="whats-new" className="py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.7 }}
          className="mb-12"
        >
          <p className="text-gb-neon text-[11px] tracking-[0.3em] mb-3">08 · WHAT'S NEW</p>
          <h2 className="font-title text-6xl sm:text-7xl lg:text-8xl uppercase text-gb-pale leading-[0.85]">
            Standing on
            <br />
            <span className="text-outline">shoulders</span>
          </h2>
        </motion.div>

        {/* references */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.7 }}
          className="border-2 border-gb-line bg-gb-card p-6 sm:p-8 mb-4"
          data-testid="whatsnew-references"
        >
          <div className="flex items-center gap-3 mb-6">
            <span className="inline-flex items-center justify-center w-9 h-9 border-2 border-gb-accent/50 text-gb-neon">
              <Library size={17} />
            </span>
            <h3 className="font-title text-2xl sm:text-3xl text-gb-pale">REFERENCES</h3>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {REFS.map((r) => (
              <div key={r.k} className="border border-gb-line bg-gb-bg p-4" data-testid="whatsnew-reference">
                <p className="text-[11px] font-bold tracking-[0.1em] text-gb-neon mb-1.5">{r.k}</p>
                <p className="text-[11px] leading-relaxed text-gb-light/85">{r.d}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* what replicant adds */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.7 }}
          className="border-2 border-gb-line bg-gb-card p-6 sm:p-8"
          data-testid="whatsnew-adds"
        >
          <div className="flex items-center gap-3 mb-6">
            <span className="inline-flex items-center justify-center w-9 h-9 border-2 border-gb-accent/50 text-gb-neon">
              <Sparkles size={17} />
            </span>
            <h3 className="font-title text-2xl sm:text-3xl text-gb-pale">WHAT REPLICANT CITY ADDS</h3>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {ADDS.map((a, i) => (
              <motion.div
                key={a.t}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ delay: (i % 2) * 0.06, duration: 0.5 }}
                className="border border-gb-line bg-gb-bg p-4"
                data-testid={`whatsnew-add-${i + 1}`}
              >
                <p className="text-[11px] font-bold tracking-[0.15em] text-gb-neon mb-1.5">{a.t}</p>
                <p className="text-[11px] leading-relaxed text-gb-light/85">{a.d}</p>
              </motion.div>
            ))}
          </div>
          <p className="mt-6 text-[10px] tracking-[0.2em] text-gb-muted">
            AGENTS, NOT ENTITIES · RESULTS DESCRIBE THIS MODEL ONLY
          </p>
        </motion.div>
      </div>
    </section>
  );
}